import lodashGet from "lodash/get";
import { PillarTableColumnArrayCount } from "shared/components/pillar-table/columns/array/PillarTableColumnArrayCount";
import { PillarTableColumnArrayFilter } from "shared/components/pillar-table/columns/array/PillarTableColumnArrayFilter";
import { PillarTableColumnArrayFind } from "shared/components/pillar-table/columns/array/PillarTableColumnArrayFind";
import { PillarTableColumnArrayJoin } from "shared/components/pillar-table/columns/array/PillarTableColumnArrayJoin";
import { PillarTableColumnArraySum } from "shared/components/pillar-table/columns/array/PillarTableColumnArraySum";
import { type PillarTableRowContextType } from "shared/components/pillar-table/row/PillarTableRowContext";
import { type DeepKeys } from "shared/types/deepkeys";

export const pillarTableColumnArrayMapOutput = <T,>({
  arrayValue,
  outputPath,
}: {
  arrayValue: T[];
  outputPath?: string;
}) => {
  if (outputPath === undefined || outputPath === "") {
    return arrayValue;
  }
  return arrayValue.map((item) => lodashGet(item, outputPath));
};

export const pillarTableColumnArrayValue = ({
  rowContext,
  cellValuePath,
}: {
  rowContext?: PillarTableRowContextType;
  cellValuePath?: string;
}) => {
  if (Array.isArray(rowContext?.rowValue)) {
    return rowContext?.rowValue;
  }
  return lodashGet(rowContext?.rowValue, cellValuePath ?? "") ?? [];
};

// Unwrap arrays so that deep paths can be applied to array element types
export type UnwrapArray<T> =
  T extends ReadonlyArray<infer U>
    ? UnwrapArray<U>
    : T extends Array<infer U>
      ? UnwrapArray<U>
      : T;

// Type helper: get the value type at a deep path P ("A.B.C") within T
// Array-aware: if T is an array, we apply the path to its element type
export type DeepValue<T, P extends string> =
  T extends ReadonlyArray<infer E>
    ? DeepValue<E, P>
    : T extends Array<infer E>
      ? DeepValue<E, P>
      : P extends keyof T & string
        ? T[P]
        : P extends `${infer K}.${infer Rest}`
          ? K extends keyof T & string
            ? DeepValue<T[K], Rest>
            : never
          : never;

// Deep paths that resolve to an array type on T
export type PathToArray<T> =
  DeepKeys<T> extends infer K
    ? K extends string
      ? DeepValue<T, K> extends ReadonlyArray<unknown>
        ? K
        : DeepValue<T, K> extends Array<unknown>
          ? K
          : never
      : never
    : never;

const PillarTableColumnArray = () => {
  throw new Error(
    "PillarTableColumnArray is a static class and cannot be instantiated.",
  );
};

PillarTableColumnArray.Join = PillarTableColumnArrayJoin;
PillarTableColumnArray.Count = PillarTableColumnArrayCount;
PillarTableColumnArray.Sum = PillarTableColumnArraySum;
PillarTableColumnArray.Find = PillarTableColumnArrayFind;
PillarTableColumnArray.Filter = PillarTableColumnArrayFilter;
export default PillarTableColumnArray;
